import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";

import LayoutWrapper from "../../pages/Layout/LayoutWrapper";
import { LoadingIndicator } from "../LoadingIndicator/LoadingIndicator";
import { Unauthorized } from "../../pages/Unauthorized/Unauthorized";
import { useUserContext } from "../../../providers/UserProvider";
import { useSession } from "../../../hooks/useSession/useSession";
import { getCurrentRoute } from "../../../lib/navigation-helpers";
import {
    clearLoginFlowMarkers,
    hasActiveRegion,
    isDirectRegionConfigured,
    SILENT_PROBE_DONE_KEY,
    startRegionResolution
} from "../../../lib/region-auth";
import { isEuEnabledCached, resolveEuEnabled } from "../../../feature-flags/euFeatureFlag";

// Adopted from https://raw.githubusercontent.com/okta/okta-react/master/samples/routing/react-router-dom-v6/src/components/SecureRoute.tsx
// due to incomptability between current react-router v6 and recommended by the Okta devs at
// https://github.com/okta/okta-react/issues/178#issuecomment-1150407182.
export const RequireAuth = () => {
    const location = useLocation();
    const { isAuthenticated, signIn, isLoading: isLoadingSession } = useSession();
    const {
        hasAdminAccess,
        profile,
        organizationId,
        organizationName,
        isLoading: isLoadingUserDetails,
        getAvailableRoutes
    } = useUserContext();

    useEffect(() => {
        const euEnabled = isEuEnabledCached();

        if (isAuthenticated) {
            if (euEnabled) {
                // Token exists -> its issuer is now the source of truth; drop the
                // transient pre-auth target so it can never override the token region,
                // and clear the loop guards so a later re-auth probes fresh.
                clearLoginFlowMarkers();
            }
            return;
        }
        if (isLoadingSession) {
            return;
        }

        void (async () => {
            // Resolve the flag BEFORE branching. The latch is empty for every user on
            // their first load, so `euEnabled` is false then — short-circuiting to
            // signIn() here would mean resolveEuEnabled() never runs, the latch is
            // never written, and enable_eu could never activate at all.
            //
            // `euEnabled` and the module-scope oktaAuth in App.tsx were both fixed from
            // the latch at page load. If the resolved value differs, oktaAuth is built
            // for the other mode: starting an authorization now would hand the callback
            // a page whose oktaAuth has a different issuer/client, and the code exchange
            // would fail. Reload first so oktaAuth is rebuilt from the latch
            // resolveEuEnabled just wrote. Converges after one reload — the reloaded
            // page reads the same latch it decided from.
            const euResolved = await resolveEuEnabled();
            if (euResolved !== euEnabled) {
                window.location.reload();
                return;
            }
            // An incomplete direct-region configuration behaves as flag-off rather than
            // half-enabled — see isDirectRegionConfigured.
            if (!euResolved || !isDirectRegionConfigured()) {
                signIn();
                return;
            }
            // Direct-region: OktaAuth is built against the token iss or the pending
            // target from broker resolve, else the default issuer. A real login_hint
            // is owned by bootstrapRegionFromLoginHint(), which reloads once resolved.
            if (!hasActiveRegion()) {
                // Nonempty, not just present: bootstrapRegionFromLoginHint() treats an
                // empty value as no hint and resolves nothing, so `has()` would leave
                // "?login_hint=" with neither region resolution nor sign-in started —
                // a permanent spinner. Mirror its check so an empty hint falls through
                // to the cold-open sign-in below.
                const hasLoginHint = Boolean(new URLSearchParams(window.location.search).get("login_hint"));
                if (hasLoginHint) {
                    return;
                }
            }
            // Once the cold-open probe has run, handleCallbackError deliberately KEEPS
            // the probe marker set. So if the user comes back here afterwards (Back out
            // of the broker page, or retyping the URL in the same tab) with still no
            // region resolved, signIn() would go INTERACTIVE against the default issuer
            // — a raw region login, which AC5 forbids, and which would have an EU user
            // typing their email into NA Okta. Send them to the broker instead.
            if (!hasActiveRegion() && window.sessionStorage.getItem(SILENT_PROBE_DONE_KEY) === "1") {
                startRegionResolution();
                return;
            }
            // Cold open (new tab, bookmark, deep link) with no region resolved: probe the
            // default issuer silently before involving the broker. Tokens are per tab, so
            // a user who already holds an Okta session must not be shown the broker email
            // page. signIn() sends prompt=none while the probe marker is unset; on
            // login_required, handleCallbackError routes to the broker (single ingress).
            signIn();
        })();
    }, [isAuthenticated, isLoadingSession, signIn]);

    useEffect(() => {
        if (process.env.REACT_APP_GAINSIGHT_ENV_KEY !== "local" && profile && !isLoadingUserDetails) {
            window.aptrinsic(
                "identify",
                //User Fields
                {
                    id: profile.id, // Required for logged in app users
                    email: profile.email,
                    firstName: profile.firstName,
                    lastName: profile.lastName
                },
                //Account Fields
                {
                    id: organizationId, //Required
                    name: organizationName
                }
            );
        }
    }, [profile, organizationId, organizationName, isLoadingUserDetails]);

    if (!isAuthenticated || isLoadingUserDetails || isLoadingSession) {
        return <LoadingIndicator logo />;
    }

    if (!hasAdminAccess) {
        const urlWithParams = new URL(process.env.REACT_APP_HOME_APP_URL || "");
        urlWithParams.searchParams.append("login_hint", `${profile?.email}`);
        urlWithParams.searchParams.append("orgId", `${organizationId}`);

        window.location.href = urlWithParams.toString();

        return <LoadingIndicator logo />;
    }

    const availableRoutes = getAvailableRoutes();
    const currentRoute = getCurrentRoute({ routes: availableRoutes || [], pathname: location.pathname });

    if (!currentRoute && location.pathname !== "/") {
        return (
            <LayoutWrapper>
                <Unauthorized />
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper>
            <Outlet />
        </LayoutWrapper>
    );
};
